/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

// eslint-disable-next-line import/extensions
import agentMentionsSpotlightImage from './chat-mode-menu/filtered-apps/assets/agent-mentions-spotlight.png';
import { mentionAgentsSpotlightAcknowledgementStore } from './MentionAgentsSpotlightAcknowledgementStore';
import { type MentionAgentsSpotlightPostOfficeMessage } from './MentionAgentsSpotlightPostOfficeMessage';
import { MentionAgentsSpotlightVisibilityTracker } from './MentionAgentsSpotlightVisibilityTracker';
import messages from './messages';

const { mentionAgentsOnboardingFeatureId: MENTION_AGENTS_ONBOARDING_FEATURE_ID } =
	mentionAgentsSpotlightAcknowledgementStore;

type AgentMentionsOnboardingSpotlightProps = {
	isVisible: boolean;
	onDismiss: (source: 'cross-icon' | 'ok-button') => void;
	postOfficeMessage?: MentionAgentsSpotlightPostOfficeMessage;
	children: React.ReactNode;
};

const OFFSET: [number, number] = [-170, 0];

export const AgentMentionsOnboardingSpotlight = ({
	isVisible,
	onDismiss,
	postOfficeMessage,
	children,
}: AgentMentionsOnboardingSpotlightProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const popoverContent = (
		<MentionAgentsSpotlightVisibilityTracker featureId={MENTION_AGENTS_ONBOARDING_FEATURE_ID}>
			<PopoverContent
				dismiss={() => onDismiss('cross-icon')}
				isVisible={isVisible}
				placement="top-end"
				offset={OFFSET}
			>
				<SpotlightCard testId="agent-mentions-onboarding-spotlight">
					<SpotlightHeader>
						<SpotlightHeadline>
							{formatMessage(messages.agentMentionsOnboardingSpotlightTitle)}
						</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={() => onDismiss('cross-icon')} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightMedia>
						<Image
							src={agentMentionsSpotlightImage as string}
							alt=""
							width="288px"
							height="138px"
						/>
					</SpotlightMedia>
					<SpotlightBody>
						{formatMessage(messages.agentMentionsOnboardingSpotlightBody)}
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={() => onDismiss('ok-button')}>
								{formatMessage(messages.agentMentionsOnboardingSpotlightOk)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</MentionAgentsSpotlightVisibilityTracker>
	);

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			{postOfficeMessage ? (
				<postOfficeMessage.PostOfficeMessageInstance
					messageInstanceId={postOfficeMessage.messageInstanceId}
				>
					{() => popoverContent}
				</postOfficeMessage.PostOfficeMessageInstance>
			) : null}
		</PopoverProvider>
	);
};
